import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import type { UseFormRegister, FieldErrors, FieldValues, Path } from 'react-hook-form';

import RequiredMark from '@/shared/components/ui/RequiredMark';

interface IFormTextAreaProps<T extends FieldValues> {
  name: Path<T>;
  label?: ReactNode;
  placeholder?: string;
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  showRequiredMark?: boolean;
  validation?: {
    required?: string | boolean;
    minLength?: { value: number; message: string };
    maxLength?: { value: number; message: string };
    pattern?: { value: RegExp; message: string };
    validate?: Record<string, (value: string) => string | boolean>;
  };
  icon?: LucideIcon;
  disabled?: boolean;
  rows?: number;
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
  className?: string;
  containerClassName?: string;
  preventEmptySpaces?: boolean;
}

const FormTextArea = <T extends FieldValues>({
  name,
  label,
  placeholder,
  register,
  errors,
  validation,
  icon: Icon,
  disabled = false,
  rows = 3,
  resize = 'none',
  className = '',
  containerClassName = '',
  preventEmptySpaces = true,
  showRequiredMark = true,
}: IFormTextAreaProps<T>) => {
  const error = errors[name];
  const errorMessage = error?.message as string;

  const resizeClasses = {
    none: 'resize-none',
    vertical: 'resize-y',
    horizontal: 'resize-x',
    both: 'resize',
  };

  // Enhanced validation to prevent empty spaces
  const enhancedValidation = {
    ...validation,
    validate: {
      ...validation?.validate,
      noEmptySpaces: (value: string) => {
        if (!preventEmptySpaces) {
          return true;
        }
        if (value && value.trim() !== value) {
          return 'Cannot start or end with spaces';
        }
        return true;
      },
      noOnlySpaces: (value: string) => {
        if (!preventEmptySpaces) {
          return true;
        }
        if (value && value.trim().length === 0) {
          return 'Cannot contain only spaces';
        }
        return true;
      },
    },
  };

  const registerProps = register(name, enhancedValidation);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let value = e.target.value;

    // Prevent leading spaces
    if (preventEmptySpaces && value.startsWith(' ')) {
      value = value.trimStart();
      e.target.value = value;
    }

    registerProps.onChange(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    // Trim whitespace on blur
    if (preventEmptySpaces) {
      const trimmedValue = e.target.value.trim();
      if (trimmedValue !== e.target.value) {
        e.target.value = trimmedValue;
        // Trigger onChange to update form state
        const changeEvent = {
          ...e,
          target: { ...e.target, value: trimmedValue },
        } as React.ChangeEvent<HTMLTextAreaElement>;
        registerProps.onChange(changeEvent);
      }
    }
    registerProps.onBlur(e);
  };

  return (
    <div className={`${containerClassName}`}>
      {label && (
        <label htmlFor={name} className='block text-base font-semibold text-gray-700 mb-2'>
          <span className='inline-flex items-center gap-1'>
            {label}
            {showRequiredMark && !!validation?.required && <RequiredMark />}
          </span>
        </label>
      )}
      <div className='relative'>
        {Icon && (
          <div className='absolute top-3 left-4 flex items-start pointer-events-none'>
            <Icon className='h-5 w-5 text-gray-400' />
          </div>
        )}
        <textarea
          id={name}
          rows={rows}
          ref={registerProps.ref}
          name={registerProps.name}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`
            w-full ${Icon ? 'pl-12' : 'pl-4'} pr-4 py-2
            border-2 rounded-xl shadow-sm ${resizeClasses[resize]}
            focus:outline-none focus:ring-2 focus:ring-theme-500 focus:border-transparent
            transition-all duration-200
            ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-200'}
            ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}
            ${className}
          `}
          placeholder={placeholder}
          disabled={disabled}
        />
      </div>
      {error && <p className='mt-2 text-sm text-red-600 font-medium'>{errorMessage}</p>}
    </div>
  );
};

export default FormTextArea;
